import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import ArtistCard from "./ArtistCard";
import { useApp } from "../context/AppContext";

const EMPTY = { name:"", specialty:"", email:"", password:"", telephone:"", country:"", address:"", website:"", intro:"", biography:"", introAr:"", biographyAr:"" };

function validate(form, profile, works) {
  const e = {};
  if (!form.intro.trim())     e.intro     = true;
  if (!form.biography.trim()) e.biography = true;
  if (!profile)               e.profile   = true;
  if (works.length < 3)       e.works     = true;
  return e;
}

function FieldError({ show, msg }) {
  if (!show) return null;
  return <span className="field-error" style={{ color:"var(--danger,#e53e3e)", fontSize:"0.82rem", marginTop:4, display:"block" }}>{msg}</span>;
}

function Register() {
  const { t, lang } = useApp();
  const [form, setForm]     = useState(EMPTY);
  const [profile, setProfile] = useState(null);
  const [works, setWorks]   = useState([]);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [errs, setErrs]     = useState({});
  const [created, setCreated] = useState(null);
  const navigate = useNavigate();

  const onChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errs[e.target.name]) setErrs({ ...errs, [e.target.name]: false });
  };

  const onSubmit = async e => {
    e.preventDefault(); setError("");
    const v = validate(form, profile, works);
    if (Object.keys(v).length) {
      setErrs(v);
      document.querySelector(".field-error")?.scrollIntoView({ behavior:"smooth", block:"center" });
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k,v]) => data.append(k, v));
      data.append("profileImage", profile);
      works.forEach(f => data.append("images", f));
      const res = await axios.post("/api/artists", data, { headers:{ "Content-Type":"multipart/form-data" } });
      localStorage.setItem("spar_token", res.data.token);
      localStorage.setItem("spar_user", JSON.stringify(res.data.artist));
      setCreated(res.data.artist);
      window.scrollTo({ top:0, behavior:"smooth" });
    } catch(err) {
      setError(err.response?.data?.error || "Registration failed.");
    } finally { setBusy(false); }
  };

  if (created) {
    const preview = {
      ...created,
      _id: created._id || created.id,
      profileImage: created.profileImage || (profile && URL.createObjectURL(profile)),
      images: created.images?.length ? created.images : works.map(f => URL.createObjectURL(f)),
    };
    return (
      <section className="section container">
        <div className="form-wrap center">
          <div className="alert alert-success">{t("welcomeBack")}, {created.name}!</div>
          <div style={{ maxWidth:380, margin:"24px auto" }}><ArtistCard artist={preview} /></div>
          <div style={{ display:"flex", gap:12, justifyContent:"center", marginTop:20 }}>
            <Link className="btn btn-primary" to="/artists">{t("seeAll")}</Link>
            <Link className="btn btn-outline" to="/edit-profile">{t("editProfile") || "Edit Profile"}</Link>
          </div>
        </div>
      </section>
    );
  }

  const inputClass = (field) => `input${errs[field] ? " input-error" : ""}`;
  const textClass  = (field) => `textarea${errs[field] ? " input-error" : ""}`;
  const fileClass  = (field) => `file${errs[field] ? " input-error" : ""}`;

  return (
    <>
      <div className="page-band">
        <div className="container"><h1>{t("joinTitle")}</h1><p>{t("joinSub")}</p></div>
      </div>
      <section className="section container">
        <form className="form-wrap card-panel" onSubmit={onSubmit}>
          {error && <div className="alert alert-error">{error}</div>}

          <div className="grid-2">
            <div className="field">
              <label>{t("fullName")} <span className="req">*</span></label>
              <input className="input" name="name" value={form.name} onChange={onChange} required />
            </div>
            <div className="field">
              <label>{t("specialty")} <span className="req">*</span></label>
              <input className="input" name="specialty" value={form.specialty} onChange={onChange} placeholder={t("specialtyPh")} required />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>{t("email")} <span className="req">*</span></label>
              <input className="input" type="email" name="email" value={form.email} onChange={onChange} required />
            </div>
            <div className="field">
              <label>{t("password")} <span className="req">*</span></label>
              <input className="input" type="password" name="password" value={form.password} onChange={onChange} required minLength={6} />
              <div className="hint">{t("passwordHint")}</div>
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>{t("country")}</label>
              <input className="input" name="country" value={form.country} onChange={onChange} />
            </div>
            <div className="field">
              <label>{t("telephone")}</label>
              <input className="input" name="telephone" value={form.telephone} onChange={onChange} />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>{t("address")}</label>
              <input className="input" name="address" value={form.address} onChange={onChange} />
            </div>
            <div className="field">
              <label>{t("website")}</label>
              <input className="input" name="website" value={form.website} onChange={onChange} placeholder="https://" />
            </div>
          </div>

          <div className="field">
            <label>{t("shortIntro")} <span className="req">*</span></label>
            <textarea className={textClass("intro")} name="intro" value={form.intro} onChange={onChange} placeholder={t("introPh")} />
            <FieldError show={errs.intro} msg={t("introRequired") || "Short intro is required."} />
          </div>

          <div className="field">
            <label>{t("biography")} <span className="req">*</span></label>
            <textarea className={textClass("biography")} name="biography" value={form.biography} onChange={onChange} style={{ minHeight:130 }} placeholder={t("bioPh")} />
            <FieldError show={errs.biography} msg={t("bioRequired") || "Biography is required."} />
          </div>

          <div className="card-panel" style={{ background:"var(--cream)", borderRadius:10, padding:"20px 22px", marginTop:4 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <i className="fa-solid fa-language" style={{ color:"var(--brand)" }} />
              <span style={{ fontWeight:700 }}>{lang==="ar" ? "الحقول العربية" : "Arabic Fields"}</span>
              <span className="hint" style={{ margin:0, fontSize:"0.8rem" }}>{t("arFieldsHint")}</span>
            </div>
            <div className="field">
              <label dir="rtl">{t("shortIntroAr")}</label>
              <textarea className="textarea" name="introAr" value={form.introAr} onChange={onChange} dir="rtl" style={{ fontFamily:"Cairo, sans-serif" }} placeholder={t("introArPh")} />
            </div>
            <div className="field">
              <label dir="rtl">{t("biographyAr")}</label>
              <textarea className="textarea" name="biographyAr" value={form.biographyAr} onChange={onChange} dir="rtl" style={{ fontFamily:"Cairo, sans-serif", minHeight:130 }} placeholder={t("bioArPh")} />
            </div>
          </div>

          <div className="field">
            <label>{t("profilePhoto")} <span className="req">*</span></label>
            <div className="hint">{t("photoHint") || "Upload a clear personal profile photo."}</div>
            <input className={fileClass("profile")} type="file" accept="image/*" onChange={e => { setProfile(e.target.files[0]||null); setErrs(prev => ({ ...prev, profile:false })); }} />
            {profile && <div className="thumb-row"><img src={URL.createObjectURL(profile)} alt="preview" /></div>}
            <FieldError show={errs.profile} msg={t("photoRequired") || "Profile photo is required."} />
          </div>

          <div className="field">
            <label>{t("workSamples")} <span className="req">*</span></label>
            <div className="hint">{t("worksHint") || "Upload at least 3 artworks for your card to appear on the Artists page."}</div>
            <input className={fileClass("works")} type="file" accept="image/*" multiple onChange={e => { setWorks(Array.from(e.target.files).slice(0,4)); setErrs(prev => ({ ...prev, works:false })); }} />
            {works.length > 0 && <div className="thumb-row">{works.map((f,i)=><img key={i} src={URL.createObjectURL(f)} alt={`work ${i+1}`} />)}</div>}
            <div className="hint">{works.length}/4 selected.</div>
            <FieldError show={errs.works} msg={t("worksRequired") || "At least 3 artworks are required."} />
          </div>

          <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
            {busy ? t("submitting") : t("submitProfile")}
          </button>
          <p className="hint center" style={{ marginTop:14 }}>{t("alreadyMember")} <Link to="/login">{t("signInLink")}</Link></p>
        </form>
      </section>
    </>
  );
}
export default Register;
